/**
 * Dashboard API authentication: fetch RSA public key, encrypt credentials, login.
 *
 * Matches Java/Kotlin:
 *   Cipher.getInstance("RSA/ECB/OAEPWithSHA-256AndMGF1Padding")
 *
 * JVM default for that cipher uses SHA-256 for the OAEP digest and SHA-1 for MGF1.
 * Web Crypto API cannot set MGF1 separately, so we use node-forge (same as forge docs).
 */
import forge from "node-forge";

const apiUrl = process.env.REACT_APP_API_URL ?? "";

export type AuthenticationCertificateResponse = {
  success?: string | boolean;
  payload?: string;
  message?: string;
  statusCode?: number;
};

export type AuthenticationLoginResponse = {
  token?: string;
  message?: string;
  success?: string | boolean;
  payload?: unknown;
  statusCode?: number;
};

function getDashboardApiCredentials(): { userName: string; password: string } {
  return {
    userName: process.env.REACT_APP_DASHBOARD_API_USERNAME?.trim(),
    password: process.env.REACT_APP_DASHBOARD_API_PASSWORD?.trim(),
  };
}

/** SPKI (base64 DER) from /authentication/application/certificate → forge RSA public key */
function importRsaPublicKeyFromSpkiBase64(
  spkiBase64: string,
): forge.pki.rsa.PublicKey {
  const der = forge.util.decode64(spkiBase64.replace(/\s/g, ""));
  const pem =
    "-----BEGIN PUBLIC KEY-----\n" +
    forge.util.encode64(der, 64) +
    "\n-----END PUBLIC KEY-----";
  return forge.pki.publicKeyFromPem(pem) as forge.pki.rsa.PublicKey;
}

/**
 * RSA-OAEP encrypt compatible with Java OAEPWithSHA-256AndMGF1Padding defaults:
 * digest SHA-256, MGF1 SHA-1.
 */
function encryptWithPublicKey(
  plainText: string,
  publicKey: forge.pki.rsa.PublicKey,
): string {
  const encrypted = publicKey.encrypt(
    forge.util.encodeUtf8(plainText),
    "RSA-OAEP",
    {
      md: forge.md.sha256.create(),
      mgf1: {
        md: forge.md.sha1.create(),
      },
    },
  );
  return forge.util.encode64(encrypted);
}

export async function fetchApplicationCertificate(): Promise<string> {
  const response = await fetch(
    `${apiUrl}/authentication/application/certificate`,
    {
      method: "GET",
      headers: { Accept: "application/json" },
    },
  );

  const data = (await response
    .json()
    .catch(() => ({}))) as AuthenticationCertificateResponse;

  if (!response.ok) {
    const err = new Error(
      data?.message ||
        `Failed to load authentication certificate (${response.status})`,
    ) as Error & { status?: number; body?: unknown };
    err.status = response.status;
    err.body = data;
    throw err;
  }

  const payload = data?.payload;
  if (typeof payload !== "string" || !payload.trim()) {
    throw new Error(
      "Authentication certificate response did not include a public key",
    );
  }

  return payload.trim();
}

export async function encryptDashboardCredentials(
  userName: string,
  password: string,
  publicKeySpkiBase64: string,
): Promise<{ userName: string; password: string }> {
  const publicKey = importRsaPublicKeyFromSpkiBase64(publicKeySpkiBase64);
  return {
    userName: encryptWithPublicKey(userName, publicKey),
    password: encryptWithPublicKey(password, publicKey),
  };
}

export async function authenticationLogin(body: {
  userName: string;
  password: string;
}): Promise<AuthenticationLoginResponse> {
  const response = await fetch(`${apiUrl}/authentication/login`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  const data = (await response
    .json()
    .catch(() => ({}))) as AuthenticationLoginResponse;

  if (!response.ok) {
    const err = new Error(
      data?.message || `Authentication login failed (${response.status})`,
    ) as Error & { status?: number; body?: unknown };
    err.status = response.status;
    err.body = data;
    throw err;
  }

  return data;
}

/** Resolves API bearer token from login response (supports common payload shapes). */
export function extractAuthToken(
  response: AuthenticationLoginResponse,
): string | undefined {
  if (typeof response?.token === "string" && response.token) {
    return response.token;
  }
  const payload = response?.payload;
  if (payload && typeof payload === "object" && payload !== null) {
    const nested = payload as { token?: string };
    if (typeof nested.token === "string" && nested.token) {
      return nested.token;
    }
  }
  if (typeof payload === "string" && payload) {
    return payload;
  }
  return undefined;
}

/**
 * Full dashboard auth: certificate → encrypt credentials → POST /authentication/login
 */
export async function dashboardAuthenticationLogin(): Promise<AuthenticationLoginResponse> {
  const { userName, password } = getDashboardApiCredentials();

  const publicKeySpki = await fetchApplicationCertificate();
  const encrypted = await encryptDashboardCredentials(
    userName,
    password,
    publicKeySpki,
  );
  return authenticationLogin(encrypted);
}
